import React, { useEffect, useRef } from "react";
import { PictureResult } from "./PictureGameRun.tsx";
import { PictureArt } from "./PictureArt.tsx";
import { usePictureText } from "./pictureText.ts";
import { firstTryCount, missedFacts, TILE_COUNT, wrongTries } from "./pictureGame.ts";
import { cardFaces } from "../cardFaces.ts";
import { findBySlug } from "../../Shared/Seo/routeSeo.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

/** The worksheets the end screen points at after the plain quiz: the printable cousins of this game (ADR 0058). */
export const COLOR_BY_NUMBER_SLUGS = ["/color-by-number-addition", "/color-by-number-multiplication"];

interface PictureGameSummaryProps {
    result: PictureResult;
    /** Best score for the operations played, on this device, after this board. */
    best: number | null;
    isNewBest: boolean;
    /** The plain quiz for the operation chosen, by slug; null when there is none to link. */
    quizSlug: string | null;
    onPlayAgain(): void;
}

/**
 * The finished picture (ADR 0133 §4, §5): the whole picture as an image named by its
 * caption, the caption in words, the tiles right first time out of 25, the best, the
 * facts that needed the answer shown, and where to practise away from the game. Takes
 * the focus when it appears, so a keyboard or screen reader user lands on it.
 */
export function PictureGameSummary({ result, best, isNewBest, quizSlug, onPlayAgain }: PictureGameSummaryProps) {
    const t = useTranslation();
    const pictureText = usePictureText();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        ref.current?.focus({ preventScroll: true });
    }, []);

    const caption = pictureText.caption(result.picture);
    const missed = missedFacts(result.tiles);
    const wrong = wrongTries(result.tiles);
    const links = [quizSlug, ...COLOR_BY_NUMBER_SLUGS].flatMap((slug) => {
        const route = slug ? findBySlug(slug) : undefined;
        return route ? [{ slug: route.slug, text: route.h1 ?? route.slug }] : [];
    });

    return (
        <div className="quizSummary pictureSummary" ref={ref} tabIndex={-1}>
            <p className="pictureSummaryHeading">{t("flashCards.pictureFinal")}</p>
            <div className="pictureBoard pictureBoard--done">
                <PictureArt id={result.picture} label={caption} />
            </div>
            <p className="pictureSummaryCaption" aria-hidden="true">
                {caption}
            </p>

            <p className="quizSummaryScoreLabel pictureSummaryScore">
                {format(t("flashCards.pictureScoreTemplate"), { n: firstTryCount(result.tiles), total: TILE_COUNT })}
            </p>
            <p className="quizSummaryCounts">{format(t("flashCards.pictureSummaryWrongTemplate"), { n: wrong })}</p>

            {best !== null && (
                <p className={`quizSummaryBest${isNewBest ? " quizSummaryBest--new" : ""}`}>
                    {isNewBest ? t("flashCards.newBest") : format(t("flashCards.yourBestTemplate"), { n: best })}
                </p>
            )}

            {missed.length > 0 && (
                <div className="quizSummaryMisses">
                    <h3>{t("flashCards.worthAnotherLookHeading")}</h3>
                    <ul>
                        {missed.map((problem, i) => {
                            const faces = cardFaces(problem);
                            return (
                                <li key={i}>
                                    <span className="quizSummaryMissProblem">
                                        {faces.front} = {faces.back}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <button type="button" className="btn btn-primary pictureSummaryPlayAgain" onClick={onPlayAgain}>
                {t("flashCards.playAgain")}
            </button>

            {links.length > 0 && (
                <div className="picturePractice">
                    <h3>{t("flashCards.pictureMoreHeading")}</h3>
                    <ul>
                        {links.map((link) => (
                            <li key={link.slug}>
                                <a href={link.slug}>{link.text}</a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default PictureGameSummary;
